"use client"

import React, { useState, useRef, useEffect } from 'react'
import Image from 'next/image'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Button } from '@/shared/components/ui/button'
import { Label } from '@/shared/components/ui/label'
import { Textarea } from '@/shared/components/ui/textarea'
import { Play, FileText, Mic } from 'lucide-react'
import VoiceRecordingModal from '@/features/photos/components/voice-recording-modal'
import { UploadBatchProgressIndicator } from '@/features/photos/components/upload-batch-progress'
import type { DirectUploadBatchProgress } from '@/shared/types/direct-upload-progress.type'
import { uploadBasePhotosDirect } from '@/features/base/lib/upload-base-photos-client'
import successToast from '@/shared/lib/toast/success.toast'
import errorToast from '@/shared/lib/toast/error.toast'
import { authFetch } from '@/shared/lib/auth-fetch'

const EMPTY_FILE_ARRAY: File[] = []

function FilePreviewItem({
  file,
  isVideo,
  isPdf,
  onRemove,
}: {
  file: File
  isVideo: boolean
  isPdf?: boolean
  onRemove: () => void
}) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)

  useEffect(() => {
    const url = URL.createObjectURL(file)
    setPreviewUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [file])

  if (!previewUrl && !isPdf) return null

  return (
    <div className="relative group">
      {isPdf ? (
        <div className="w-full h-20 rounded border overflow-hidden bg-muted flex items-center justify-center gap-2 p-2">
          <FileText className="w-8 h-8 text-muted-foreground" />
          <span className="text-xs truncate">{file.name}</span>
        </div>
      ) : isVideo ? (
        <div className="relative w-full h-20 rounded border overflow-hidden bg-black">
          <video src={previewUrl!} className="w-full h-full object-cover" muted />
          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30">
            <Play className="w-8 h-8 text-white fill-white" />
          </div>
        </div>
      ) : (
        <Image
          src={previewUrl!}
          alt={`Preview ${file.name}`}
          className="w-full h-20 object-cover rounded border"
          width={80}
          height={80}
        />
      )}
      <button
        onClick={onRemove}
        className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600 z-10"
      >
        ×
      </button>
    </div>
  )
}

interface AddBasePhotoModalProps {
  isOpen: boolean
  onClose: () => void
  regionId: number
  onUploadSuccess?: () => void
  initialFiles?: File[]
  /** Section title / componentName for Photos Sections; defaults to "Base Photos" for timeline tab */
  componentName?: string
}

export default function AddBasePhotoModal({
  isOpen,
  onClose,
  regionId,
  onUploadSuccess,
  initialFiles = EMPTY_FILE_ARRAY,
  componentName = 'Base Photos',
}: AddBasePhotoModalProps) {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [description, setDescription] = useState('')
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState<DirectUploadBatchProgress | null>(null)
  const [filePercents, setFilePercents] = useState<number[]>([])
  const [isVoiceModalOpen, setIsVoiceModalOpen] = useState(false)
  const galleryInputRef = useRef<HTMLInputElement>(null)
  const submittedRef = useRef(false)

  useEffect(() => {
    if (isOpen && initialFiles.length > 0) {
      setSelectedFiles(initialFiles)
    }
    if (!isOpen) {
      setUploadProgress(null)
      setFilePercents([])
    }
  }, [isOpen, initialFiles])

  const validateFile = (file: File): string | null => {
    const fileName = file.name.toLowerCase()
    const lastDot = fileName.lastIndexOf('.')
    const fileExtension = lastDot >= 0 ? fileName.substring(lastDot) : ''

    const unsupportedVideoExtensions = ['.avi', '.mkv', '.flv', '.wmv', '.3gp', '.divx', '.xvid']
    if (unsupportedVideoExtensions.includes(fileExtension)) {
      return `Video "${file.name}" format is not supported. Please convert to MP4, MOV, WebM, or M4V format.`
    }
    const unsupportedVideoMimeTypes = [
      'video/x-msvideo',
      'video/x-matroska',
      'video/x-flv',
      'video/x-ms-wmv',
      'video/3gpp',
    ]
    if (unsupportedVideoMimeTypes.includes(file.type.toLowerCase())) {
      return `Video "${file.name}" format is not supported. Please convert to MP4, MOV, WebM, or M4V format.`
    }
    const isImage = file.type.startsWith('image/')
    const isVideo = file.type.startsWith('video/')
    const isPdf = file.type === 'application/pdf' || fileExtension === '.pdf'
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.tiff', '.heic', '.heif']
    const videoExtensions = ['.mov', '.mp4', '.webm', '.m4v', '.ts']
    const isImageByExt = imageExtensions.includes(fileExtension)
    const isVideoByExt = videoExtensions.includes(fileExtension)
    if (!isImage && !isVideo && !isPdf && !isImageByExt && !isVideoByExt) {
      return `File "${file.name}" is not a supported image, video, or PDF format`
    }
    const MAX_IMAGE_SIZE = 50 * 1024 * 1024
    const MAX_VIDEO_SIZE = 500 * 1024 * 1024
    if ((isImage || isImageByExt) && file.size > MAX_IMAGE_SIZE) {
      return `Image "${file.name}" is too large. Maximum size is 50MB`
    }
    if ((isVideo || isVideoByExt) && file.size > MAX_VIDEO_SIZE) {
      return `Video "${file.name}" is too large. Maximum size is 500MB`
    }
    return null
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (files) {
      const fileArray = Array.from(files)
      const validFiles: File[] = []
      const errors: string[] = []
      const currentTotal = selectedFiles.reduce((s, f) => s + f.size, 0)
      const MAX_TOTAL = 1024 * 1024 * 1024

      fileArray.forEach((file) => {
        if (currentTotal + file.size > MAX_TOTAL) {
          errors.push('Total file size would exceed 1GB limit')
          return
        }
        const err = validateFile(file)
        if (err) errors.push(err)
        else validFiles.push(file)
      })
      if (errors.length > 0) errors.forEach((e) => errorToast(e))
      if (validFiles.length > 0) setSelectedFiles((prev) => [...prev, ...validFiles])
      event.target.value = ''
    }
  }

  const handleSubmit = async () => {
    if (submittedRef.current) return
    if (selectedFiles.length === 0) {
      errorToast('Please select at least one photo or video')
      return
    }
    try {
      submittedRef.current = true
      setIsUploading(true)
      setUploadProgress(null)
      setFilePercents(selectedFiles.map(() => 0))
      const result = await uploadBasePhotosDirect(regionId, selectedFiles, {
        description: description.trim() || undefined,
        componentName,
        onProgress: setUploadProgress,
        onFileProgress: (fileIndex, pctWithinFile) => {
          setFilePercents((prev) => {
            const base = prev.length === selectedFiles.length ? [...prev] : selectedFiles.map(() => 0)
            base[fileIndex] = pctWithinFile
            return base
          })
        },
      })
      if (result.success && result.data && result.data.length > 0) {
        const count = result.data.length
        const errors = Array.isArray(result.errors) ? result.errors : []
        const skippedDuplicates = Array.isArray(result.skippedDuplicates) ? result.skippedDuplicates : []
        successToast(`Successfully uploaded ${count} ${count === 1 ? 'item' : 'items'}.`)
        if (skippedDuplicates.length > 0) {
          errorToast(`Skipped ${skippedDuplicates.length} duplicate ${skippedDuplicates.length === 1 ? 'file' : 'files'}.`)
        }
        if (errors.length > 0) {
          errorToast(
            `Some files failed to upload: ${errors.slice(0, 3).join(' ')}${errors.length > 3 ? ' ...' : ''}`
          )
        }
        onUploadSuccess?.()
        onClose()
      } else {
        const msg = 'message' in result ? result.message : 'Failed to upload. Please try again.'
        errorToast(msg)
      }
    } catch (error) {
      console.error('Error uploading base photos:', error)
      errorToast(error instanceof Error ? error.message : 'An error occurred.')
    } finally {
      submittedRef.current = false
      setIsUploading(false)
      setUploadProgress(null)
      setFilePercents([])
    }
  }

  const handleCancel = () => {
    setSelectedFiles([])
    setDescription('')
    setUploadProgress(null)
    setFilePercents([])
    onClose()
  }

  const removeFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index))
  }

  const handleVoiceSave = async (audioBlob: Blob) => {
    setIsVoiceModalOpen(false)
    const f = new File([audioBlob], 'recording.webm', { type: audioBlob.type || 'audio/webm' })
    const formData = new FormData()
    formData.append('audio', f)
    try {
      const res = await authFetch('/ai/transcribe', { method: 'POST', body: formData, credentials: 'include' })
      const data = await res.json().catch(() => ({}))
      if (res.ok && data.success && data.data?.transcription) {
        const transcript = String(data.data.transcription)
        setDescription((prev) => (prev.trim() ? `${prev} ${transcript}` : transcript))
      } else {
        errorToast('message' in data ? data.message : 'Transcription failed')
      }
    } catch (e) {
      errorToast(e instanceof Error ? e.message : 'Transcription error')
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleCancel()}>
      <DialogContent className="sm:max-w-[540px]">
        <DialogHeader>
          <DialogTitle>Add Photos</DialogTitle>
          <DialogDescription>
            Select photos or videos to add. You can add multiple files at once.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Files</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {selectedFiles.map((file, i) => (
                <FilePreviewItem
                  key={`${file.name}-${i}`}
                  file={file}
                  isVideo={file.type.startsWith('video/')}
                  isPdf={file.type === 'application/pdf'}
                  onRemove={() => removeFile(i)}
                />
              ))}
              <button
                type="button"
                onClick={() => galleryInputRef.current?.click()}
                className="flex flex-col items-center justify-center w-20 h-20 rounded border border-dashed border-muted-foreground/40 hover:border-muted-foreground/60 text-muted-foreground hover:text-foreground transition-colors"
              >
                <span className="text-2xl">+</span>
                <span className="text-xs">Add</span>
              </button>
            </div>
            <input
              ref={galleryInputRef}
              type="file"
              accept="image/*,video/*,.pdf,application/pdf"
              multiple
              onChange={handleFileChange}
              className="hidden"
            />
          </div>
          <div>
            <div className="flex items-center justify-between gap-2 mb-1">
              <Label htmlFor="base-photo-desc">Description (optional)</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setIsVoiceModalOpen(true)}
                className="gap-1.5"
              >
                <Mic className="w-4 h-4" /> Voice
              </Button>
            </div>
            <Textarea
              id="base-photo-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Type or use Voice to add a description..."
              className="mt-1"
              rows={3}
            />
          </div>
          {isVoiceModalOpen && (
            <VoiceRecordingModal
              isOpen
              onClose={() => setIsVoiceModalOpen(false)}
              onSave={handleVoiceSave}
              autoStart
            />
          )}
        </div>
        {isUploading && selectedFiles.length > 0 && (
          <UploadBatchProgressIndicator
            progress={
              uploadProgress ?? {
                completedCount: 0,
                totalCount: selectedFiles.length,
                activeIndex: 1,
                activeFileName: selectedFiles[0]?.name ?? null,
              }
            }
            fileNames={selectedFiles.map((f) => f.name)}
            filePercents={filePercents.length === selectedFiles.length ? filePercents : undefined}
          />
        )}
        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={selectedFiles.length === 0 || isUploading}
          >
            {isUploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
